// Keep in sync with realtime-node/src/activitySessions.js. Connection heartbeats
// report this age; they must never create a new user interaction.
export const SESSION_IDLE_MS = 5 * 60 * 1000;
export const ACTIVITY_REPORT_INTERVAL_MS = 5000;

export function createSessionActivityTracker({
  userId,
  onChange,
  doc = document,
  win = window,
}) {
  const key = `node_session_activity:${userId}`;
  const isVisible = () => doc.visibilityState === 'visible' && doc.hasFocus();
  let lastInteraction = null;
  let reportTimer = null;
  let lastReport = -Infinity;
  let stopped = false;

  try {
    const stored = win.sessionStorage.getItem(key);
    const timestamp = stored === null ? NaN : Number(stored);
    if (Number.isFinite(timestamp) && timestamp > 0 && timestamp <= Date.now()) {
      lastInteraction = timestamp;
    }
  } catch { /* Tracking also works when storage is unavailable. */ }

  const rememberInteraction = () => {
    lastInteraction = Date.now();
    try { win.sessionStorage.setItem(key, String(lastInteraction)); } catch { /* Best effort. */ }
  };

  // Opening a foreground tab starts a session. Reloading an existing tab keeps
  // its last interaction instead of granting another five minutes of activity.
  if (lastInteraction === null && isVisible()) rememberInteraction();

  const getState = () => ({
    visible: !stopped && isVisible(),
    idleMs: lastInteraction === null
      ? SESSION_IDLE_MS
      : Math.max(0, Date.now() - lastInteraction),
  });

  const report = () => {
    if (reportTimer !== null) win.clearTimeout(reportTimer);
    reportTimer = null;
    if (stopped) return;
    lastReport = Date.now();
    onChange(getState());
  };

  const handleInteraction = (event) => {
    if (!event.isTrusted || !isVisible()) return;
    const wasIdle = getState().idleMs >= SESSION_IDLE_MS;
    rememberInteraction();
    const remaining = ACTIVITY_REPORT_INTERVAL_MS - (Date.now() - lastReport);
    if (wasIdle || remaining <= 0) report();
    else if (reportTimer === null) reportTimer = win.setTimeout(report, remaining);
  };

  // Visibility/focus changes report state without pretending to be new input.
  const handleVisibility = () => report();
  const handlePageHide = () => {
    if (reportTimer !== null) win.clearTimeout(reportTimer);
    reportTimer = null;
    if (!stopped) onChange({ ...getState(), visible: false });
  };
  const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'touchmove'];
  events.forEach((event) => doc.addEventListener(event, handleInteraction, { capture: true, passive: true }));
  doc.addEventListener('visibilitychange', handleVisibility);
  win.addEventListener('focus', handleVisibility);
  win.addEventListener('blur', handleVisibility);
  win.addEventListener('pageshow', handleVisibility);
  win.addEventListener('pagehide', handlePageHide);

  return {
    getState,
    stop() {
      stopped = true;
      if (reportTimer !== null) win.clearTimeout(reportTimer);
      events.forEach((event) => doc.removeEventListener(event, handleInteraction, true));
      doc.removeEventListener('visibilitychange', handleVisibility);
      win.removeEventListener('focus', handleVisibility);
      win.removeEventListener('blur', handleVisibility);
      win.removeEventListener('pageshow', handleVisibility);
      win.removeEventListener('pagehide', handlePageHide);
    },
  };
}
